import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BodyAreaChips,
  type Technique,
  TreatmentPicker,
} from '@/features/admin-terminal/components/stock-footage/stock-clip-tags-editor';
import { cn } from '@/lib/utils';
import type { ServiceFootageChoices } from '@borradh-workspace/contracts';
import { bodyRegionLabels, bodyRegionValues } from '@borradh-workspace/labels';
import type {
  BodyRegion,
  TreatmentCatalogueEntry,
} from '@borradh-workspace/labels';
import {
  Check,
  ChevronsUpDown,
  PersonStanding,
  WandSparkles,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { ServiceTreatmentPicker } from './service-treatment-picker';
import { useServiceTreatmentOptions } from './use-service-treatment-options';

export const defaultServiceFootageChoices = (): ServiceFootageChoices => ({
  treatments: [],
  techniqueSlug: null,
  agentSlugs: [],
  regions: [],
  footageActions: [],
  footageOtherFields: ['treatment'],
});
type Scope = {
  treatmentSlug: string;
  areaStatus: 'unknown' | 'selected' | 'not_applicable' | 'independent';
  regions: BodyRegion[];
  procedureNeutral?: boolean;
};
const emptyScope = (): Scope => ({
  treatmentSlug: '',
  areaStatus: 'unknown',
  regions: [],
});

/** A service may leave its area open, or restrict each treatment to named areas. */
function ServiceAreaPicker({
  id,
  value,
  other = false,
  disabled,
  onChange,
}: {
  id: string;
  value: BodyRegion[];
  other?: boolean;
  disabled: boolean;
  onChange: (regions: BodyRegion[], other?: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="flex items-center gap-2">
        <PersonStanding
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
        Treatment area (optional)
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-12 w-full justify-between rounded-xl bg-background px-3.5 font-normal shadow-none"
          >
            <span className="truncate">
              {value.length
                ? value.map((region) => bodyRegionLabels[region]).join(', ')
                : other
                  ? 'Other'
                  : 'Not set'}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] rounded-xl p-0"
        >
          <Command>
            <CommandInput placeholder="Search areas…" />
            <CommandList>
              <CommandEmpty>No area matches.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="not set null"
                  onSelect={() => {
                    onChange([]);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value.length || other ? 'opacity-0' : 'opacity-100'
                    )}
                  />
                  Not set
                </CommandItem>
                <CommandItem
                  value="other unlisted area"
                  onSelect={() => {
                    onChange([], true);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      other ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  Other
                </CommandItem>
                {bodyRegionValues.map((region) => (
                  <CommandItem
                    key={region}
                    value={bodyRegionLabels[region]}
                    onSelect={() =>
                      onChange(
                        value.includes(region)
                          ? value.filter((r) => r !== region)
                          : [...value, region]
                      )
                    }
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        value.includes(region) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {bodyRegionLabels[region]}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((region) => (
            <Button
              key={region}
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              className="h-7 gap-1.5 rounded-full px-2.5 text-xs font-normal"
              aria-label={`Remove ${bodyRegionLabels[region]}`}
              onClick={() => onChange(value.filter((r) => r !== region))}
            >
              {bodyRegionLabels[region]}
              <X className="size-3" aria-hidden="true" />
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** One catalogue choice supplies the procedure/device and its broader technique. */
export function TreatmentSelectionFields({
  scopes,
  onChange,
  catalogue,
  disabled = false,
  idPrefix = 'treatment',
  clip = false,
}: {
  scopes: Scope[];
  onChange: (scopes: Scope[]) => void;
  catalogue: TreatmentCatalogueEntry[];
  disabled?: boolean;
  idPrefix?: string;
  clip?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const displayed = scopes.length
    ? [...scopes, ...(adding ? [emptyScope()] : [])]
    : [emptyScope()];
  const change = (index: number, patch: Partial<Scope>) => {
    const next = displayed
      .map((s, i) => (i === index ? { ...s, ...patch } : s))
      .filter((s) => s.treatmentSlug);
    setAdding(false);
    onChange(next);
  };
  return (
    <div className="space-y-6">
      {displayed.map((scope, index) => {
        const treatment = catalogue.find((t) => t.slug === scope.treatmentSlug);
        const id = `${idPrefix}-${index}`;
        return (
          <div
            key={id}
            className="space-y-4"
            role="group"
            aria-label={`Treatment ${index + 1}`}
          >
            <div className="space-y-2">
              <Label htmlFor={`${id}-choice`}>
                {index ? 'Additional treatment' : 'Treatment'}
              </Label>
              <TreatmentPicker
                id={`${id}-choice`}
                value={scope.treatmentSlug || null}
                noneLabel="Other / not specified"
                techniques={catalogue
                  .filter(
                    (t) =>
                      t.slug === scope.treatmentSlug ||
                      !scopes.some((s) => s.treatmentSlug === t.slug)
                  )
                  .map((t) => ({
                    slug: t.slug,
                    displayName: t.displayName,
                    aliases: t.aliases,
                  }))}
                disabled={disabled}
                onChange={(slug) =>
                  change(index, {
                    treatmentSlug: slug ?? '',
                    procedureNeutral: false,
                  })
                }
              />
            </div>
            {treatment?.kind === 'family' ? (
              <p className="text-sm text-muted-foreground">
                Matches footage marked for this broad treatment family.
              </p>
            ) : null}
            {clip && treatment?.kind === 'family' ? (
              <div className="flex items-start gap-2">
                <Checkbox
                  id={`${id}-neutral`}
                  checked={scope.procedureNeutral ?? false}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    change(index, { procedureNeutral: checked === true })
                  }
                />
                <Label htmlFor={`${id}-neutral`}>
                  Reviewed as procedure-neutral for this family
                </Label>
              </div>
            ) : null}
            {clip ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor={`${id}-area`}>Treatment area</Label>
                  <Select
                    disabled={disabled || !scope.treatmentSlug}
                    value={scope.areaStatus}
                    onValueChange={(status) =>
                      change(index, {
                        areaStatus: status as Scope['areaStatus'],
                        regions: status === 'selected' ? scope.regions : [],
                      })
                    }
                  >
                    <SelectTrigger id={`${id}-area`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Not specified</SelectItem>
                      <SelectItem
                        value="selected"
                        disabled={!scope.regions.length}
                      >
                        Specific areas selected
                      </SelectItem>
                      <SelectItem
                        value={clip ? 'independent' : 'not_applicable'}
                      >
                        {clip
                          ? 'Area-independent'
                          : 'Not applicable to this treatment'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {scope.treatmentSlug &&
                ['unknown', 'selected'].includes(scope.areaStatus) ? (
                  <BodyAreaChips
                    value={scope.regions}
                    disabled={disabled}
                    onChange={(regions) =>
                      change(index, {
                        regions,
                        areaStatus: regions.length ? 'selected' : 'unknown',
                      })
                    }
                  />
                ) : null}
                {scope.treatmentSlug && scope.areaStatus === 'unknown' ? (
                  <p className="text-sm text-muted-foreground">
                    {clip
                      ? 'Choose visible areas, or review this shot as area-independent.'
                      : 'No area restriction.'}
                  </p>
                ) : null}
                {scope.treatmentSlug &&
                scope.areaStatus === 'not_applicable' ? (
                  <p className="text-sm text-muted-foreground">
                    No area restriction.
                  </p>
                ) : null}
              </>
            ) : scope.treatmentSlug ? (
              <ServiceAreaPicker
                id={`${id}-area`}
                value={scope.regions}
                disabled={disabled}
                onChange={(regions) =>
                  change(index, {
                    regions,
                    areaStatus: regions.length ? 'selected' : 'unknown',
                  })
                }
              />
            ) : null}
            {(clip ? scopes.length > 1 : scope.treatmentSlug || adding) ? (
              <Button
                type="button"
                variant="ghost"
                disabled={disabled}
                onClick={() => {
                  setAdding(false);
                  onChange(scopes.filter((_, i) => i !== index));
                }}
              >
                Remove treatment
              </Button>
            ) : null}
          </div>
        );
      })}
      {scopes.length > 0 && !adding && scopes.length < 12 ? (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => setAdding(true)}
        >
          Add another treatment
        </Button>
      ) : null}
    </div>
  );
}
export function ServiceFootageFields({
  value,
  onChange,
  catalogue = [],
  serviceName = '',
  organizationId,
  disabled = false,
  idPrefix = 'service-footage',
}: {
  value: ServiceFootageChoices;
  onChange: (value: ServiceFootageChoices) => void;
  techniques?: Technique[];
  catalogue?: TreatmentCatalogueEntry[];
  serviceName?: string;
  organizationId?: string;
  disabled?: boolean;
  idPrefix?: string;
}) {
  const options = useServiceTreatmentOptions(organizationId, serviceName);
  const availableCatalogue = options.data?.catalogue ?? catalogue;
  const scope = value.treatments.length === 1 ? value.treatments[0] : null;
  const areas = scope?.regions ?? value.regions;
  const otherTreatment = value.footageOtherFields.includes('treatment');
  const otherArea = value.footageOtherFields.includes('body_area');
  const treatment = availableCatalogue.find(
    (entry) => entry.slug === scope?.treatmentSlug
  );
  const otherFields = (field: 'treatment' | 'body_area', other: boolean) =>
    [
      ...value.footageOtherFields.filter((item) => item !== field),
      ...(other ? [field] : []),
    ] as ServiceFootageChoices['footageOtherFields'];
  return (
    <div className="overflow-hidden rounded-2xl border bg-muted/20 shadow-sm">
      <div className="space-y-3 p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <Label
            htmlFor={`${idPrefix}-choice`}
            className="flex items-center gap-2"
          >
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <WandSparkles className="size-4" aria-hidden="true" />
            </span>
            Treatment
          </Label>
          <span className="rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground">
            Optional
          </span>
        </div>
        <ServiceTreatmentPicker
          id={`${idPrefix}-choice`}
          value={otherTreatment ? 'other' : (scope?.treatmentSlug ?? null)}
          serviceName={serviceName}
          catalogue={availableCatalogue}
          recommendedSlugs={options.data?.recommendedSlugs}
          disabled={disabled}
          onChange={(slug) =>
            onChange({
              ...value,
              footageOtherFields: otherFields('treatment', slug === 'other'),
              treatments:
                slug && slug !== 'other'
                  ? [
                      {
                        treatmentSlug: slug,
                        areaStatus: 'unknown',
                        regions: [],
                      },
                    ]
                  : [],
              regions: [],
            })
          }
        />
        {options.isFetching ? (
          <p role="status" className="text-xs text-muted-foreground">
            Finding treatments…
          </p>
        ) : options.isError ? (
          <p role="status" className="text-xs text-muted-foreground">
            Could not find options from the service name. You can still choose
            from the catalogue.
          </p>
        ) : null}
        {value.treatments.length > 1 ? (
          <p className="text-sm text-muted-foreground">
            Choose one treatment to replace the saved combination.
          </p>
        ) : treatment?.kind === 'family' ? (
          <p className="text-xs text-muted-foreground">
            Matches footage marked for this broad treatment family.
          </p>
        ) : null}
      </div>
      <div className="border-t bg-background p-5 sm:p-6">
        <ServiceAreaPicker
          id={`${idPrefix}-area`}
          value={areas}
          other={otherArea}
          disabled={disabled || value.treatments.length > 1}
          onChange={(regions, other = false) =>
            onChange({
              ...value,
              footageOtherFields: otherFields('body_area', other),
              regions: scope ? [] : regions,
              treatments: scope
                ? [
                    {
                      ...scope,
                      regions,
                      areaStatus: regions.length ? 'selected' : 'unknown',
                    },
                  ]
                : value.treatments,
            })
          }
        />
      </div>
    </div>
  );
}
